import React from 'react';
import './Privacy.css';

function Privacy() {
  return (
    <div className="page privacy-page">
      <h1>Privacy Policy</h1>
      <p className="last-updated"><strong>Last Updated:</strong> March 2026</p>

      <p>Welcome to Open Health. We are committed to protecting your privacy and ensuring the security of your personal health information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.</p>

      <h2>1. Information We Collect</h2>
      <p>We collect the following types of health and personal data:</p>
      <ul>
        <li><strong>Wearable Device Data:</strong> Heart rate, steps, sleep duration, activity data, and other metrics from connected wearable devices (collected via Garmin Connect API and other supported providers)</li>
        <li><strong>Lab Results:</strong> Blood test results and other laboratory data that you upload in PDF format</li>
        <li><strong>Questionnaire Responses:</strong> Health questionnaires including symptom tracking, menstrual cycle logs, wellness check-ins, and other self-reported health information</li>
        <li><strong>Account Information:</strong> Email address, name, and basic profile information necessary to create and manage your account</li>
        <li><strong>Usage Data:</strong> Information about how you interact with our service, including access times, pages viewed, and features used</li>
      </ul>

      <h2>2. How We Collect Information</h2>
      <p>We collect information through the following methods:</p>
      <ul>
        <li><strong>Direct Submission:</strong> Information you provide directly through our application, including questionnaire responses and uploaded lab results</li>
        <li><strong>Wearable Device Integration:</strong> Data automatically synced from connected wearable devices through authorized API integrations (e.g., Garmin Connect)</li>
        <li><strong>Third-Party Services:</strong> Data obtained through third-party services that you authorize, such as open-wearables platform for wearable device data aggregation</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We use your health data for the following purposes:</p>
      <ul>
        <li>To provide personalized health insights and comprehensive health tracking across multiple data sources</li>
        <li>To aggregate and correlate data from wearables, lab results, and questionnaires for holistic health monitoring</li>
        <li>To improve our services, develop new features, and enhance user experience</li>
        <li>To communicate with you about your account, service updates, and important health-related information</li>
        <li>To ensure the security and integrity of our platform</li>
      </ul>

      <h2>4. Data Sharing and Third-Party Services</h2>
      <p>We work with the following third-party services to provide our functionality:</p>
      <ul>
        <li><strong>Garmin:</strong> We access your wearable device data through Garmin Connect API with your explicit authorization. Garmin's privacy policy applies to data collected by Garmin.</li>
        <li><strong>Open Wearables Platform:</strong> We use the open-wearables service as an intermediary for wearable device data integration. This service processes and normalizes data from multiple wearable providers.</li>
      </ul>
      <p><strong>We do not sell, rent, or trade your personal health data to third parties for marketing or commercial purposes.</strong></p>
      <p>We may share aggregated, anonymized data for research purposes, but this data cannot be used to identify individual users.</p>

      <h2>5. Data Storage and Security</h2>
      <p>Your data is stored securely on our servers using industry-standard security measures, including:</p>
      <ul>
        <li>Encryption of data in transit and at rest</li>
        <li>Secure authentication and access controls</li>
        <li>Regular security assessments and updates</li>
        <li>Limited access to personal data on a need-to-know basis</li>
      </ul>
      <p>While we implement robust security measures, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your information to the best of our ability.</p>

      <h2>6. Your Rights and Choices</h2>
      <p>You have the following rights regarding your personal data:</p>
      <ul>
        <li><strong>Access:</strong> Request access to your personal data and receive a copy of the information we hold about you</li>
        <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
        <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal and operational requirements</li>
        <li><strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
        <li><strong>Disconnect Devices:</strong> Disconnect your wearable devices at any time through your account settings</li>
        <li><strong>Opt-Out:</strong> Opt out of certain data collection or processing activities where applicable</li>
      </ul>
      <p>To exercise these rights, please contact us using the information provided in the "Contact Us" section below.</p>

      <h2>7. Data Retention</h2>
      <p>We retain your personal data for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When you delete your account, we will delete or anonymize your personal data, except where we are required to retain it for legal, regulatory, or legitimate business purposes.</p>

      <h2>8. Children's Privacy</h2>
      <p>Our service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately so we can delete such information.</p>

      <h2>9. International Data Transfers</h2>
      <p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. We take appropriate safeguards to ensure your data is protected in accordance with this Privacy Policy.</p>

      <h2>10. Changes to This Privacy Policy</h2>
      <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:</p>
      <ul>
        <li>Posting the updated policy on this page with a new "Last Updated" date</li>
        <li>Sending you an email notification (if you have provided an email address)</li>
        <li>Displaying a prominent notice in our application</li>
      </ul>
      <p>Your continued use of our service after such changes constitutes your acceptance of the updated Privacy Policy.</p>

      <h2>11. Contact Us</h2>
      <div className="contact-info">
        <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
        <p><strong>Email:</strong> <a href="mailto:fmml@mit.edu">fmml@mit.edu</a></p>
      </div>

      <h2>12. Compliance</h2>
      <p>We are committed to complying with applicable data protection laws, including but not limited to:</p>
      <ul>
        <li>General Data Protection Regulation (GDPR) for users in the European Union</li>
        <li>Health Insurance Portability and Accountability Act (HIPAA) where applicable</li>
        <li>Other relevant privacy and data protection regulations in your jurisdiction</li>
      </ul>
    </div>
  );
}

export default Privacy;

